import { CredentialWithId, DetailsCardItem } from "../types/credentialTypes";

/**
 * Builds the details card data array from credential details.
 *
 * Transforms a credential object into an array of display-ready card items
 * for rendering in the credential details panel. Each item includes a header,
 * formatted text value, and styling properties.
 *
 * The following fields are included:
 * - Client
 * - Portal ID, Email, Username
 * - Location ID
 * - Status flags (Active Status, Queue Enabled)
 * - Subject Regex, Credential ID
 * - Timestamps (Created At, Updated At) and Updated By
 *
 * @param credential - The credential object containing all credential data
 * @returns An array of DetailsCardItem objects ready for UI rendering.
 *          Missing values are displayed as "--"
 *
 * @example
 * ```tsx
 * const detailsData = buildCredentialDetailsData(selectedCredential);
 * // Returns: [{ header: "Client", text: "Acme Corp", ... }, ...]
 * ```
 */
export const buildCredentialDetailsData = (
  credential: CredentialWithId,
): DetailsCardItem[] => {
  return [
    {
      header: "Client",
      text: credential.client_name || "--",
      textColor: "text-primaryGray-11",
      fontWeight: "font-semibold",
    },
    {
      header: "Portal ID",
      text: credential.portal_id || "--",
      textColor: "text-primaryGray-11",
      fontWeight: "font-semibold",
    },
    {
      header: "Location ID",
      text: credential.location_id || "--",
      textColor: "text-primaryGray-11",
      fontWeight: "font-semibold",
    },
    {
      header: "Email",
      text: credential.email || "--",
      textColor: "text-primaryGray-11",
      fontWeight: "font-semibold",
    },
    {
      header: "Username",
      text: credential.username || "--",
      textColor: "text-primaryGray-11",
      fontWeight: "font-semibold",
    },
    {
      header: "Active Status",
      text: credential.is_active ? "Yes" : "No",
      textColor: "text-primaryGray-11",
      fontWeight: "font-semibold",
    },
    {
      header: "Queue Enabled",
      text: credential.is_queue ? "Yes" : "No",
      textColor: "text-primaryGray-11",
      fontWeight: "font-semibold",
    },
    {
      header: "Subject Regex",
      text: credential.subject_regex || "--",
      textColor: "text-primaryGray-11",
      fontWeight: "font-semibold",
    },
    {
      header: "Credential ID",
      text: credential.credential_id || "--",
      textColor: "text-primaryGray-11",
      fontWeight: "font-semibold",
    },
    {
      header: "Created At",
      text: credential.created_at
        ? new Date(credential.created_at).toLocaleDateString()
        : "--",
      textColor: "text-primaryGray-11",
      fontWeight: "font-semibold",
    },
    {
      header: "Updated At",
      text: credential.updated_at
        ? new Date(credential.updated_at).toLocaleDateString()
        : "--",
      textColor: "text-primaryGray-11",
      fontWeight: "font-semibold",
    },
    {
      header: "Updated By",
      text: credential.updated_by || "--",
      textColor: "text-primaryGray-11",
      fontWeight: "font-semibold",
    },
  ];
};
